import { useMyProfiles } from '@/hooks/useProfile';
import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "@stores/authStore";
import { useLogout } from "@hooks/useAuth";
import { useState, useEffect, useCallback } from "react";
import { cn } from "@lib/cn";
import { AbakoLogo } from "@components/ui/AbakoLogo";
import { Separator } from "@components/ui/Separator";
import { AvailabilityPopover } from "@components/features/availability/AvailabilityPopover";
import { NotificationPopover } from "@components/features/notifications/NotificationPopover";
import { useNotifications } from "@hooks/useNotifications";
import { useDaoConfig } from "@/hooks/useDaoConfig";

/**
 * FRONTEND STATUS
 *
 * OK
 * Related tasks: Sprint 1, FeatureImplementación Mejora vista directa proyectos
 */

/**
 * Sidebar - Left navigation sidebar matching Figma design
 *
 * Figma specs:
 * - w-[200px], full height
 * - bg-[var(--base-surface-2,#231f1f)]
 * - border-right: 1px solid var(--base-border,#3d3d3d)
 * - Padding: 16px
 *
 * TOP: Logo + Notification bell with indicator
 * NAV MENU:
 *   - Each item: flex row, gap-12px, px-16px, py-12px, rounded-12px
 *   - Active: bg-[var(--base-fill-1,#333)], text-[var(--state-brand-active,#36d399)]
 *   - Inactive: text-[var(--text-dark-primary,#f5f5f5)], hover bg-[var(--base-fill-1)]
 *   - Icon (24px) + Label text (Inter Medium, 16px)
 *
 * BOTTOM: User avatar + name + role with online dot
 *
 * Matches Figma component from Team Definition screen (1039:10096)
 */
export function Sidebar({ notificationStatus }: { notificationStatus?: string }) {
  useMyProfiles();
  const user = useAuthStore((state) => state.user);
  const profiles = useAuthStore((state) => state.profiles);
  const profileName = user?.role === 'client' ? profiles?.client?.name : profiles?.worker?.name;
  const navigate = useNavigate();
  const { mutate: logout, isPending: loggingOut, error: logoutError } = useLogout();
  const [isOpen, setIsOpen] = useState(false);

  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const toggleAvailability = useCallback(() => setAvailabilityOpen((v) => !v), []);
  const closeAvailability = useCallback(() => setAvailabilityOpen(false), []);

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const toggleNotifications = useCallback(
    () => setNotificationsOpen((v) => !v),
    [],
  );
  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);

  const { config, workerRole, clientRole } = useDaoConfig();
  const isClient = user?.role === 'client';
  const isDeveloper = user?.role === 'developer';
  const role = isClient ? clientRole : isDeveloper ? workerRole : "User";
  const { data: notifications = [] } = useNotifications();
  const principal = useAuthStore((state) => state.session?.principalId);
  const hasUnread = notifications.some(n => n.readAt === null);

  const navLinks = [
    { to: "/dashboard", label: "Dashboard", icon: "ri-dashboard-line" },
    { to: "/payments", label: "Payments", icon: "ri-wallet-3-line" },
    { to: "/profile", label: "Profile", icon: "ri-user-line" },
  ];

  useEffect(() => {
    const handleEscape = (e: { key: string; }) => {
      if (e.key === "Escape" && isOpen) setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  return (
    <>
      {/* Mobile button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='lg:hidden fixed top-4 left-4 z-50 w-11 h-11 flex items-center justify-center rounded-lg bg-[var(--base-surface-2,#231f1f)] border border-[var(--base-border,#3d3d3d)] text-[var(--text-dark-primary,#f5f5f5)]'
        aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
      >
        <i className={cn("text-2xl", isOpen ? "ri-close-line" : "ri-menu-line")}></i>
      </button>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky top-0 left-0 h-screen gap-[var(--spacing-8)]",
          "w-[256px] min-w-[256px]",
          "px-[var(--spacing-7)] py-[var(--spacing-8)] flex flex-col",
          "bg-[--base-surface-2] border-r border-base-border",
          "transition-transform duration-300 z-40",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* TOP */}
        <div className='flex items-center justify-between'>
          <div className='w-full h-[48px] flex items-center gap-[--spacing-5] pl-[--spacing-7]'>
            <AbakoLogo size={12} />
            <span className='text-text-dark-primary font-semibold text-base truncate'>{config.name}</span>
          </div>

          <div className="relative">
            <button
              className="relative flex items-center justify-center"
              aria-label="Notifications"
              aria-expanded={notificationsOpen}
              aria-controls="notification-panel"
              onClick={toggleNotifications}
            >
              <i className="ri-notification-2-line w-[44px] h-[44px] flex items-center justify-center text-xl text-text-dark-secondary"></i>
              {hasUnread && (
                <span className="absolute top-[9px] right-[9px] w-[--spacing-7] h-[--spacing-7] bg-[--state-success-active] rounded-full border-2 border-[--base-fill-1]"></span>
              )}
            </button>
            <NotificationPopover key={principal} open={notificationsOpen} onClose={closeNotifications} streamStatus={notificationStatus} />
          </div>
        </div>

        {/* MAIN NAV */}
        <nav className='flex flex-col gap-[--spacing-4] flex-1'>
          <ul className='space-y-1 flex flex-col gap-[--spacing-3]'>
            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-[--spacing-7] h-fit",
                      "pl-[var(--spacing-8,16px)] pr-[var(--spacing-5,8px)] py-[var(--spacing-7,12px)]",
                      "rounded-[var(--radi-6,12px)]",
                      "font-medium text-base leading-base",
                      "transition-colors",
                      isActive
                        ? "text-[--text-dark-primary] border-2 border-[--state-brand-active]"
                        : "text-[--text-dark-secondary] hover:bg-[--base-fill-1]",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <i
                        className={cn(
                          link.icon,
                          "text-[20px]",
                          isActive ? "text-[--base-fill-5]" : "text-[--base-fill-4]",
                        )}
                      />
                      <span>{link.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
          {/* Divider */}
          <Separator orientation='horizontal' className='h-[48px]' />
        </nav>

        {/* BOTTOM BLOCK */}
        <div className='flex flex-col gap-[--spacing-4]'>
          {/* SETTINGS */}
          <NavLink
            to='/settings'
            className={({ isActive }) =>
              cn(
                "flex items-center gap-[--spacing-7] h-fit",
                "pl-[var(--spacing-8,16px)] pr-[var(--spacing-5,8px)] py-[var(--spacing-7,12px)]",
                "rounded-[var(--radi-6,12px)]",
                "font-medium text-base leading-base",
                "transition-colors",
                isActive
                  ? "text-[--text-dark-primary] border-2 border-[--state-brand-active]"
                  : "text-[--text-dark-secondary] hover:bg-[--base-fill-1]",
              )
            }
          >
            {({ isActive }) => (
              <>
                <i
                  className={cn(
                    "ri-settings-5-line",
                    "text-[20px]",
                    isActive ? "text-[--base-fill-5]" : "text-[--base-fill-4]",
                  )}
                />
                <span>Settings</span>
              </>
            )}
          </NavLink>

          {/* USER */}
          <div className={"relative py-[--spacing-3] px-[--spacing-5]"}>
            <div className='flex items-center justify-between gap-3'>
              <button
                className='flex items-center gap-[--spacing-7] min-w-0 text-left'
                onClick={isDeveloper ? toggleAvailability : undefined}
                aria-label={isDeveloper ? 'Edit availability' : undefined}
              >
                <div className='relative shrink-0'>
                  <div className='h-[32px] aspect-square rounded-full border border-[--base-border] flex items-center justify-center overflow-hidden'>
                    <i className='ri-user-smile-line text-2xl text-[--base-fill-5]'></i>
                  </div>
                  <span className='absolute bottom-0 right-0 w-2.5 h-2.5 bg-[var(--state-brand-active,#36d399)] rounded-full border-2 border-[var(--base-surface-2,#231f1f)]'></span>
                </div>

                <div className='flex-1 min-w-0 flex flex-col gap-[--spacing-2]'>
                  <p className='text-base font-medium text-[--text-dark-primary] truncate'>
                    {profileName || user?.name || "User"}
                  </p>
                  <p className='text-xs text-[--text-dark-secondary] truncate'>
                    {role}
                  </p>
                </div>
              </button>

              <button
                className='shrink-0'
                aria-label='Log out' disabled={loggingOut}
                onClick={() => logout(undefined, { onSuccess: () => navigate("/login") })}
              >
                <i className='ri-logout-box-r-line text-xl text-[--base-fill-4]'></i>
              </button>
            </div>

            {logoutError && <p role="alert">Logout failed: {logoutError.message} Please retry.</p>}
            {isDeveloper && (
              <AvailabilityPopover open={availabilityOpen} onClose={closeAvailability} />
            )}
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {isOpen && (
        <div
          className='lg:hidden fixed inset-0 bg-black/50 z-30'
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
